import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Lookups, PageResult, Row } from "../../config/types";
import { api } from "../../services/api";
import { apiUrl } from "../../config/environment";
import { useAuth } from "../../store/AuthContext";
import useGoogleCalendar, { type GoogleEvent } from "./useGoogleCalendar";
import { GoogleCalendarEvents, GoogleCalendarEventDetails } from "../../components/common/GoogleCalendarEvents";
import { display } from "../../utils/format";
import { Badge, ErrorNotice, Loading, Modal } from "../../components/common/Primitives";

function dateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function ReservationCalendar({ query, revision, lookups, canCreate, canEdit, canDelete, deletePermanently, onCreate, onEdit, onDelete }: {
  query: string;
  revision: unknown;
  lookups: Lookups;
  canCreate: boolean;
  canEdit: (row: Row) => boolean;
  canDelete: (row: Row) => boolean;
  deletePermanently: boolean;
  onCreate: (date: string) => void;
  onEdit: (row: Row) => void;
  onDelete: (row: Row) => void;
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState<Row>();
  const [selectedGoogle, setSelectedGoogle] = useState<GoogleEvent>();
  const google = useGoogleCalendar(month, revision);
  const hasPendingSync = rows.some(row => row.googleSyncStatus === "pending");
  useEffect(() => {
    if (!hasPendingSync) return;
    const timer = window.setInterval(() => setRetry(value => value + 1), 10000);
    return () => window.clearInterval(timer);
  }, [hasPendingSync]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    async function load() {
      const all: Row[] = [];
      let page = 1;
      let total = 0;
      do {
        const params = new URLSearchParams({ q: query, page: String(page++), limit: "100", sort: "date", order: "ASC" });
        const result = await api<PageResult>("/meeting-reservations?" + params, { signal: controller.signal });
        all.push(...result.items);
        total = result.total;
        if (!result.items.length) break;
      } while (all.length < total);
      if (!controller.signal.aborted) {
        setRows(all);
        setSelected(current => current ? all.find(row => row.id === current.id) : undefined);
      }
    }
    load().catch((cause: Error) => {
      if (!controller.signal.aborted) setError(cause.message);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [query, revision, retry]);
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: Math.ceil((offset + days) / 7) * 7 }, (_, index) =>
    new Date(month.getFullYear(), month.getMonth(), index - offset + 1));
  const locale = i18n.resolvedLanguage || "fr";
  const today = dateKey(new Date());
  function move(amount: number) {
    setMonth(new Date(month.getFullYear(), month.getMonth() + amount, 1));
  }
  return <div className="reservation-calendar">
    {google.error && <ErrorNotice message={t(google.error)} retry={google.refresh} />}
    <div className="calendar-toolbar">
      <div>
        <h3 aria-live="polite">{month.toLocaleDateString(locale, { month: "long", year: "numeric" })}</h3>
        {canCreate && <p>{t("Click a date to create a reservation.")}</p>}
      </div>
      <div className="calendar-navigation">
        <button className="button secondary" onClick={() => setMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>{t("Today")}</button>
        <button className="icon-button" aria-label={t("Previous month")} onClick={() => move(-1)}><ChevronLeft size={18} /></button>
        <button className="icon-button" aria-label={t("Next month")} onClick={() => move(1)}><ChevronRight size={18} /></button>
      </div>
    </div>
    {error ? <ErrorNotice message={error} retry={() => setRetry(value => value + 1)} /> : loading ? <Loading /> : <>
      <div className="calendar-scroll">
        <div className="calendar-grid">
          {Array.from({ length: 7 }, (_, index) => <div className="calendar-weekday" key={index}>{new Date(2026, 0, 5 + index).toLocaleDateString(locale, { weekday: "short" })}</div>)}
          {cells.map(date => {
            const key = dateKey(date);
            const bookings = rows.filter(row => String(row.date).slice(0, 10) === key).sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));
            const create = () => {
              if (canCreate) onCreate(key);
            };
            return <div
              key={key}
              className={"calendar-day" + (canCreate ? " can-create" : "") + (date.getMonth() !== month.getMonth() ? " outside-month" : "") + (key === today ? " is-today" : "")}
              title={canCreate ? t("Create a reservation on {{date}}", { date: date.toLocaleDateString(locale, { dateStyle: "long" }) }) : undefined}
              onClick={create}
            >
              {canCreate ? <button
                className="calendar-date-button"
                aria-label={t("Create a reservation on {{date}}", { date: date.toLocaleDateString(locale, { dateStyle: "long" }) })}
                onClick={(event) => { event.stopPropagation(); create(); }}
              ><time dateTime={key}>{date.getDate()}</time></button> : <time dateTime={key} aria-label={date.toLocaleDateString(locale, { dateStyle: "full" })}>{date.getDate()}</time>}
              {bookings.map(row => <button key={row.id} className={"calendar-booking" + (row.status === "Cancelled" ? " cancelled" : "")} onClick={(event) => { event.stopPropagation(); setSelected(row); }}>
                {row.status === "Cancelled" && <Badge>Cancelled</Badge>}
                <strong>{String(row.startTime).slice(0, 5)}–{String(row.endTime).slice(0, 5)}</strong>
                <span>{display(row, "roomId", lookups)}</span>
                <span>{String(row.title)}</span>
              </button>)}
              <GoogleCalendarEvents items={google.items.filter(item => !item.reservationId || !rows.some(row => row.id === item.reservationId))} date={date} query={query} onSelect={setSelectedGoogle} />
            </div>;
          })}
        </div>
      </div>
      {!rows.some(row => String(row.date).startsWith(dateKey(month).slice(0, 7))) && !google.items.length && <p className="calendar-empty" role="status">{t("No reservations this month.")}</p>}
    </>}
    {selected && <Modal title={String(selected.title)} wide className="reservation-details-modal" onClose={() => setSelected(undefined)}>
      <dl className="calendar-details">
        {["date", "startTime", "endTime", "roomId", "floorId", "reservedBy", "status"].map((key, index) => <div key={key}><dt>{t(["Date", "Start time", "End time", "Meeting room", "Floor", "Reserved by", "Status"][index])}</dt><dd>{key === "status" ? <Badge>{display(selected, key, lookups)}</Badge> : display(selected, key, lookups)}</dd></div>)}
      </dl>
      <div className="calendar-participants"><strong>{t("Participants")}</strong><p>{display(selected, "participantIds", lookups)}</p></div>
      {selected.googleSyncStatus && <div className="calendar-sync-status" role="status">
        <strong>{t(selected.googleSyncStatus === "synced" ? (selected.status === "Cancelled" ? "Reservation removed from Google Calendar" : "Reservation added to Google Calendar") : selected.googleSyncStatus === "error" ? "Google Calendar synchronization needs attention" : "Google Calendar synchronization pending")}</strong>
        {selected.googleSyncError && <p>{t(String(selected.googleSyncError))}</p>}
        {selected.googleSyncStatus === "error" && selected.userId === user?.id && <a className="button secondary" href={apiUrl("/auth/google/connect?returnTo=/meeting-reservations")}>{t("Reconnect Google")}</a>}
      </div>}
      {(canEdit(selected) || canDelete(selected)) && <div className="modal-actions">
        {canDelete(selected) && <button className={deletePermanently ? "button destructive" : "button secondary"} onClick={() => { onDelete(selected); setSelected(undefined); }}>{t(deletePermanently ? "Delete meeting room reservation" : "Cancel reservation")}</button>}
        {canEdit(selected) && <button className="button primary" onClick={() => { onEdit(selected); setSelected(undefined); }}>{t("Edit meeting room reservation")}</button>}
      </div>}
    </Modal>}
    {selectedGoogle && <GoogleCalendarEventDetails event={selectedGoogle} onClose={() => setSelectedGoogle(undefined)} note="Your Google events are visible only to you. They do not reserve a meeting room." />}
  </div>;
}
